import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, switchMap, take } from 'rxjs';
import { AppState } from '../store';
import { selectAccessToken } from '../store/auth/auth.selectors';
import { AuthActions } from '../store/auth/auth.actions';
import { MarketplaceTokenPersistenceService } from '../services/marketplace-token-persistence.service';
import { MARKETPLACE_AUTH_CONFIG } from '../services/marketplace-auth.config';

export const marketplaceTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const store       = inject(Store<AppState>);
  const persistence = inject(MarketplaceTokenPersistenceService);

  if (shouldSkip(req)) {
    return next(req);
  }

  // Add Delfi access_token to marketplace-service requests.
  if (req.url.includes('/marketplace-service/')) {
    return store.select(selectAccessToken).pipe(
      take(1),
      switchMap(accessToken => {
        const authReq = accessToken
          ? req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } })
          : req;
        return next(authReq).pipe(
          catchError((err: HttpErrorResponse) => handleExpiredToken(err, req, next, store, persistence)),
        );
      }),
    );
  }

  // All other requests: forward unchanged.
  return next(req);
};

function handleExpiredToken(
  err: HttpErrorResponse,
  req: import('@angular/common/http').HttpRequest<unknown>,
  next: import('@angular/common/http').HttpHandlerFn,
  store: Store<AppState>,
  persistence: MarketplaceTokenPersistenceService,
) {
  if (err.status === 401) {
    persistence.clear();
    store.dispatch(AuthActions.signOut());
    const cleanReq = req.clone({ headers: req.headers.delete('Authorization') });
    return next(cleanReq);
  }
  throw err;
}

function shouldSkip(req: import('@angular/common/http').HttpRequest<unknown>): boolean {
  return req.url.includes('/oauth/')
    || req.url.startsWith(MARKETPLACE_AUTH_CONFIG.tokenUrl)
    || req.url.startsWith(MARKETPLACE_AUTH_CONFIG.endSessionUrl);
}
